import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import messages from './messages';
import ProductCard from '../../../components/pdpV2/ProductCard';
import {
  setMattressAndFrameOption,
  setSelectedBedFrameAOVSKUs,
} from '../../../slices/pdp';
import styles from './BundleBedFrameAOV.module.scss';
import {
  MATTRESS_AND_FRAME_OPTION,
  MATTRESS_ONLY_OPTION,
  ProductCardType,
} from '../../pdpV2/constants';
import {
  DEFAULT_IN_HOME_SETUP_PRICE_CENT,
  IS_OUT_OF_STOCK,
  IS_OUT_OF_STOCK_TRUE_KEY,
  PURPLE_PREMIUM_PLUS_SMART_BASE,
  PURPLE_PREMIUM_SMART_BASE,
} from '../../../utils/common/constants';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import { getDiscountPercent, priceToString } from '../../../utils';
import AlternativeProductWrapper from '../../../components/AlternativeProductWrapper/AlternativeProductWrapper';
import { findVariantByAttributeValues } from '../../../utils/variants';
import slaConfig from '../../../components/Sla/slaConfig.json';
import { getCookie } from '../../../utils/common';
import VariantSelection from '../../pdpV2/VariantSelection';
import { BundlePdpInStore } from '../../../slices/bundlePdp/types';
import { PDPInStore } from '../../../slices/pdp/types';
import {
  BedFrameAOV,
  BundleDrupalSettings,
} from '../AddToCartBundlePdpContainer/types';
import {
  AltProductMapType,
  BedFrameAOVOption,
  BedFrameAOVVariant,
} from './types';
import { useThunkDispatch } from '../../../hooks/useThunkDispatch/useThunkDispatch';

const BundleBedFrameAOV = () => {
  const dispatch = useThunkDispatch();
  const { aovs, bundle, product } = useDrupalSettings() as BundleDrupalSettings;
  const { formatMessage, formatNumber, locale } = useIntl();

  const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);
  const [selectedSKU, setSelectedSKU] = useState('');
  const [isBedFrameSelected, setIsBedFrameSelected] = useState(false);

  const productFromDrupalSettings = bundle[0].product;

  const { selectedVariantsForBundleItemProducts } = useSelector(
    (state: { bundlePdp: BundlePdpInStore }) => state.bundlePdp,
  );

  useEffect(() => {
    setSelectedSKU(
      selectedVariantsForBundleItemProducts?.[
        productFromDrupalSettings.key
      ]?.[0]?.sku,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFromDrupalSettings.key, selectedVariantsForBundleItemProducts]);

  const { selectedBedFrameAOVSKUs, mattressAndFrameOption } = useSelector(
    (state: { pdp: PDPInStore }) => state.pdp,
  );

  const bedFrameAOVForSelectedSKU = aovs?.find(
    ({ targetSku }: { targetSku: string }) => targetSku === selectedSKU,
  )?.frames as BedFrameAOV;

  const [altProductMap, setAltProductMap] = useState<AltProductMapType>({});
  const arrayOfVariants = bedFrameAOVForSelectedSKU?.options
    ?.map(({ variants }) => variants)
    .flat();

  const initialAltProductMap = useMemo(
    () =>
      Object.fromEntries(
        bedFrameAOVForSelectedSKU?.options?.map((option) => [
          option.productId,
          option.alternativeProduct,
        ]) ?? [],
      ),
    [bedFrameAOVForSelectedSKU],
  );

  const hasAltVariantSelected =
    bedFrameAOVForSelectedSKU?.options
      ?.find((option) => option?.alternativeProduct !== undefined)
      ?.alternativeProduct?.product?.variants.find((variant) =>
        selectedBedFrameAOVSKUs.includes(variant.sku),
      ) !== undefined;

  const hasAltProductSelected =
    !!bedFrameAOVForSelectedSKU?.options?.find(({ variants }) =>
      variants.find(({ sku }) => selectedBedFrameAOVSKUs.includes(sku)),
    ) || hasAltVariantSelected;

  useEffect(
    () => {
      if (
        (!selectedBedFrameAOVSKUs.length || !hasAltProductSelected) &&
        mattressAndFrameOption === MATTRESS_AND_FRAME_OPTION &&
        bedFrameAOVForSelectedSKU
      ) {
        const initialBedFrameAOVVariant =
          bedFrameAOVForSelectedSKU?.options?.[0].variants?.filter(
            (variant) => !variant.isOutOfStock,
          )?.[0];
        if (isBedFrameSelected) {
          dispatch(
            setSelectedBedFrameAOVSKUs([initialBedFrameAOVVariant?.sku ?? '']),
          );
        }
        setAltProductMap(initialAltProductMap);
      }
    }, // Beware to add variables to dependency array manually
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      initialAltProductMap,
      selectedBedFrameAOVSKUs.length,
      hasAltProductSelected,
      mattressAndFrameOption,
      hasAltVariantSelected,
      setAltProductMap,
    ],
  );

  const onChangeProductCard = (item: string) => {
    if (!selectedBedFrameAOVSKUs.includes(item)) {
      const newValue = arrayOfVariants?.some(({ sku }) => sku === item)
        ? [item]
        : [...selectedBedFrameAOVSKUs, item];
      dispatch(setSelectedBedFrameAOVSKUs(newValue));
      setIsBedFrameSelected(true);

      dispatch(
        setMattressAndFrameOption({
          mattressAndFrameOption: MATTRESS_AND_FRAME_OPTION,
        }),
      );
    } else {
      dispatch(
        setMattressAndFrameOption({
          mattressAndFrameOption: MATTRESS_ONLY_OPTION,
        }),
      );
      dispatch(setSelectedBedFrameAOVSKUs([]));
    }
  };

  const onChangeProductVariant = ({
    sku,
    productKey,
  }: {
    sku: string;
    productKey: keyof typeof altProductMap;
  }) => {
    const selectedOption = bedFrameAOVForSelectedSKU?.options?.find(
      (option) => option.productId === productKey,
    ) as BedFrameAOVOption;

    const newBedframeAOVVariantSelected = selectedOption?.variants?.find(
      (variant) => variant.sku === sku,
    ) as BedFrameAOVVariant;

    const matchingAltProductVariant = findVariantByAttributeValues({
      variants: altProductMap[productKey]?.product?.variants ?? [],
      attributeValues: newBedframeAOVVariantSelected?.attributes,
      returnPartialMatchingIfFullMatchIsNotFound: true,
    }) as BedFrameAOVVariant;

    const matchingAltProductVariantIsOutOfStock =
      !!matchingAltProductVariant?.isOutOfStock;

    const matchingAltProductVariantSkuArray =
      hasAltVariantSelected &&
      matchingAltProductVariant &&
      !matchingAltProductVariantIsOutOfStock
        ? [matchingAltProductVariant.sku]
        : [];
    dispatch(
      setSelectedBedFrameAOVSKUs([sku, ...matchingAltProductVariantSkuArray]),
    );
  };

  const addRemoveAltProductCallback = useCallback(
    (productId: string) => {
      let arrayOfSkus = [];

      const selectedOption = bedFrameAOVForSelectedSKU?.options?.find(
        (option) => option.productId === productId,
      );

      if (!hasAltVariantSelected) {
        const bedframeAOVVariantSelected = selectedOption?.variants?.find(
          (variant) => selectedBedFrameAOVSKUs.includes(variant.sku),
        );

        const matchingAltProductVariant = findVariantByAttributeValues({
          variants: altProductMap[productId]?.product?.variants ?? [],
          attributeValues: bedframeAOVVariantSelected?.attributes ?? [],
          returnPartialMatchingIfFullMatchIsNotFound: true,
        }) as BedFrameAOVVariant;

        const altProductMasterVariant = altProductMap[
          productId
        ]?.product?.variants.find((variant) => variant.masterVariant);

        arrayOfSkus.push(
          matchingAltProductVariant?.sku ||
            altProductMasterVariant?.sku ||
            altProductMap[productId]?.product?.variants?.[0]?.sku,
        );

        arrayOfSkus.push(bedframeAOVVariantSelected?.sku);
      } else {
        const altProductVariantSelected = altProductMap[
          productId
        ]?.product?.variants.find((variant) =>
          selectedBedFrameAOVSKUs.includes(variant.sku),
        );

        arrayOfSkus = altProductVariantSelected
          ? [...selectedBedFrameAOVSKUs]?.filter(
              (selectedBedFrameAOVSKU) =>
                selectedBedFrameAOVSKU !== altProductVariantSelected.sku,
            )
          : [...selectedBedFrameAOVSKUs];
      }
      dispatch(setSelectedBedFrameAOVSKUs(arrayOfSkus as string[]));
    },
    [
      bedFrameAOVForSelectedSKU?.options,
      hasAltVariantSelected,
      dispatch,
      altProductMap,
      selectedBedFrameAOVSKUs,
    ],
  );

  const bedFrameAOVSelectedSkuOptions = bedFrameAOVForSelectedSKU?.options;

  const containerClass =
    bedFrameAOVSelectedSkuOptions && bedFrameAOVSelectedSkuOptions?.length > 0
      ? 'bedframe-options-container'
      : '';

  const defaultBedFrameAOVOption = bedFrameAOVSelectedSkuOptions?.[0];

  const alternativeBedFrameAOVOptions = bedFrameAOVSelectedSkuOptions?.filter(
    (option) => option.productId !== defaultBedFrameAOVOption?.productId,
  );

  const renderBedFrameAOVOption = (frameAOVOption: BedFrameAOVOption) => {
    const variantSelected = frameAOVOption.variants?.find((variant) =>
      selectedBedFrameAOVSKUs.includes(variant.sku),
    );
    const isProductVariantSelected = !!variantSelected;

    const isProductAltVariantSelected = !!bedFrameAOVSelectedSkuOptions
      ?.find((option) => option.productId === frameAOVOption.productId)
      ?.alternativeProduct?.product.variants.find((altProductVariant) =>
        selectedBedFrameAOVSKUs.includes(altProductVariant.sku),
      );

    const currentVariantForBedFrameAOVOption =
      variantSelected ?? frameAOVOption.variants?.[0];
    const hasVariants = frameAOVOption.variants?.length > 1;

    const altProductVariants =
      frameAOVOption?.alternativeProduct?.product?.variants;

    const foundAltProductVariant = findVariantByAttributeValues({
      variants: altProductVariants ?? [],
      attributeValues: currentVariantForBedFrameAOVOption.attributes,
      returnPartialMatchingIfFullMatchIsNotFound: true,
    }) as BedFrameAOVVariant;

    const isFoundAltProductVariantOutOfStock =
      foundAltProductVariant?.attributes?.find(
        (attribute: { name: string; value: string }) =>
          attribute.name === IS_OUT_OF_STOCK,
      )?.key === IS_OUT_OF_STOCK_TRUE_KEY;

    const hasAlternativeProduct =
      frameAOVOption?.alternativeProduct !== undefined &&
      altProductVariants &&
      altProductVariants?.length > 0 &&
      !!foundAltProductVariant &&
      !isFoundAltProductVariantOutOfStock;

    const altProductHasVariants =
      altProductVariants && altProductVariants?.length > 1;

    const currentVariantForAltProduct =
      altProductVariants?.find((variant) =>
        selectedBedFrameAOVSKUs.includes(variant.sku),
      ) ||
      foundAltProductVariant ||
      altProductVariants?.find((variant) => variant?.masterVariant ?? false);

    const altVariantToShowInProductSwitch = (altProductVariants?.find(
      (variant) => selectedBedFrameAOVSKUs.includes(variant.sku),
    ) ||
      (isProductVariantSelected &&
        findVariantByAttributeValues({
          variants: altProductVariants ?? [],
          attributeValues: currentVariantForBedFrameAOVOption.attributes,
          returnPartialMatchingIfFullMatchIsNotFound: true,
        })) ||
      altProductVariants?.[0]) as BedFrameAOVVariant;

    const isAltProductVariantSelected = !!selectedBedFrameAOVSKUs?.find(
      (currentSelectedSku) =>
        currentSelectedSku === altVariantToShowInProductSwitch?.sku,
    );

    if (currentVariantForBedFrameAOVOption) {
      const { price, discount } = currentVariantForBedFrameAOVOption;
      const discountAmount = frameAOVOption?.isAbsoluteDiscount
        ? priceToString({
            price: discount,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          })
        : `${getDiscountPercent(price?.centAmount, discount?.centAmount)}%`;
      const discountMessage =
        discount?.centAmount > 0
          ? formatMessage(messages.discountBadge, {
              discount: discountAmount,
            })
          : null;

      const inHomeSetupPricing = priceToString({
        price: {
          centAmount: (getCookie(
            process.env.CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME,
          )?.value ?? DEFAULT_IN_HOME_SETUP_PRICE_CENT) as number,
          fractionDigits: 2,
          currencyCode: 'USD',
        },
        maximumFractionDigits: 0,
        formatNumber,
        formatMessage,
      });

      const whiteGloveMessage =
        getCookie(
          process.env.OVERWRITE_PAID_IN_HOME_SHIPPING_AB_TEST_COOKIE_NAME,
        )?.value &&
        [PURPLE_PREMIUM_SMART_BASE, PURPLE_PREMIUM_PLUS_SMART_BASE].includes(
          frameAOVOption.productKey,
        ) &&
        !!slaConfig.mattress.find(
          (p) => p.key === productFromDrupalSettings.key,
        )
          ? formatMessage(messages.freeInHomeSetupWithDiscount, {
              pricing: inHomeSetupPricing,
            })
          : '';

      return (
        <>
          <div
            className={classnames(styles['frame-option'], {
              [styles['is-show-variants']]:
                isProductVariantSelected && hasVariants,
            })}
          >
            <ProductCard
              title={frameAOVOption.productName[locale]}
              id={currentVariantForBedFrameAOVOption.sku}
              name={currentVariantForBedFrameAOVOption.sku}
              description={frameAOVOption?.description?.[locale] as string}
              price={
                isAltProductVariantSelected
                  ? {
                      ...(currentVariantForAltProduct?.price ?? {}),
                      centAmount:
                        (currentVariantForAltProduct?.price.centAmount ?? 0) +
                        (currentVariantForBedFrameAOVOption?.price.centAmount ??
                          0),
                    }
                  : currentVariantForBedFrameAOVOption.price
              }
              discount={
                isAltProductVariantSelected
                  ? {
                      ...(currentVariantForAltProduct?.discount ?? {}),
                      centAmount:
                        (currentVariantForAltProduct?.discount.centAmount ??
                          0) +
                        (currentVariantForBedFrameAOVOption?.discount
                          .centAmount ?? 0),
                    }
                  : currentVariantForBedFrameAOVOption.discount
              }
              imageURL={
                isAltProductVariantSelected
                  ? (frameAOVOption?.alternativeProduct?.images?.find(
                      (image) => image.sku === currentVariantForAltProduct.sku,
                    )?.imageUrl ?? '')
                  : (currentVariantForBedFrameAOVOption?.image?.imageUrl ?? '')
              }
              isSelected={
                isProductVariantSelected || isProductAltVariantSelected
              }
              onChange={onChangeProductCard}
              badgeStyle={ProductCardType.ALTERNATIVE}
              badge={discountMessage}
              whiteGloveMessage={whiteGloveMessage}
            />
          </div>

          {((isProductVariantSelected && hasVariants) ||
            (isProductAltVariantSelected && altProductHasVariants)) && (
            <>
              {hasAlternativeProduct && (
                <div
                  className={styles['alternative-product-selector-container']}
                >
                  <AlternativeProductWrapper
                    id={frameAOVOption.productId}
                    isChecked={isAltProductVariantSelected}
                    onChecked={addRemoveAltProductCallback}
                    alternativeProduct={frameAOVOption?.alternativeProduct}
                    altVariantToShow={altVariantToShowInProductSwitch}
                  />
                </div>
              )}
              <div className={styles['variant-selector-container']}>
                <VariantSelection
                  variants={frameAOVOption.variants}
                  defaultSelectedSKU={currentVariantForBedFrameAOVOption?.sku}
                  onSelectVariant={({ sku }: { sku: string }) => {
                    onChangeProductVariant({
                      sku,
                      productKey: frameAOVOption.productId,
                    });
                  }}
                  sortColors={false}
                />
              </div>
            </>
          )}
        </>
      );
    }
    return null;
  };

  const renderBedframeAOVLink = () =>
    product?.bedfame_aov_flyout?.length ? (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a
        className={styles['heading-link']}
        href='#'
        data-micromodal-trigger={product.bedfame_aov_flyout[0].modal_id}
      >
        {product.bedfame_aov_flyout[0].label}
      </a>
    ) : null;

  return bedFrameAOVForSelectedSKU?.options &&
    bedFrameAOVForSelectedSKU?.options?.length > 0 ? (
    <div className={styles[`${containerClass}`]}>
      <div className={styles.heading}>
        <div className={styles.title}>
          {formatMessage(messages.bedFrameTitle)}
        </div>
        {renderBedframeAOVLink()}
      </div>

      {defaultBedFrameAOVOption &&
        renderBedFrameAOVOption(defaultBedFrameAOVOption)}

      {alternativeBedFrameAOVOptions?.length ? (
        <>
          <button
            type='button'
            className={classnames(styles['options-expand-btn'], {
              [styles['is-expanded']]: isOptionsExpanded,
            })}
            onClick={() => setIsOptionsExpanded((prev) => !prev)}
          >
            <span>{formatMessage(messages.expand)}</span>
            <div className={styles['options-expand-btn-icon']} />
          </button>

          {isOptionsExpanded &&
            alternativeBedFrameAOVOptions?.map((option) => (
              <Fragment key={`BedFrameAOV_${option.productId}`}>
                {renderBedFrameAOVOption(option)}
              </Fragment>
            ))}
        </>
      ) : null}
    </div>
  ) : null;
};

export default BundleBedFrameAOV;
